import { createApi } from '@reduxjs/toolkit/query/react'
import { type BaseRequest, type BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

export interface DocumentConnectorUser {
  id?: string | null
  name?: string | null
  email?: string | null
}

export interface DocumentConnectorAncestorFolder {
  id: string
  name?: string | null
}

export interface DocumentConnectorIntegration {
  type: string
  serviceId: string
  externalId: string
  externalAccountId?: string | null
  externalWorkspaceId?: string | null
}

export interface DocumentConnectorItem {
  id?: string | null
  name: string
  type: 'file' | 'folder'
  size?: number | null
  extension?: string | null
  contentType?: string | null
  createdAt?: string | null
  modifiedAt?: string | null
  createdBy?: DocumentConnectorUser | null
  modifiedBy?: DocumentConnectorUser | null
  ancestorFolders?: DocumentConnectorAncestorFolder[] | null
  itemCount?: number | null
  webUrl?: string | null
  integration?: DocumentConnectorIntegration | null
}

interface ListItemsFolderEntry {
  folder: DocumentConnectorItem
  items: DocumentConnectorItem[]
  totalCount?: number | null
  loadedCount?: number | null
  nextCursor?: string | null
}

export interface SearchItemsResult {
  items: DocumentConnectorItem[]
  totalCount?: number | null
  limit?: number | null
  offset?: number | null
  loadedCount?: number | null
  nextCursor?: string | null
}

export interface WorkspaceNode {
  id: string
  name: string
  selectable: boolean
  children: WorkspaceNode[]
}

interface ListItemsRequest extends BaseRequest {
  connector: string
  folderIds?: string[]
  limit?: number
  offset?: number
  cursor?: string
}

export type DocumentConnectorAnchor =
  | { type: 'aiAgentResult'; resultId: string; relatedToId?: string }
  | { type: 'aiResult'; resultId: string; relatedToId?: string }
  | { type: 'roomSession'; relatedToId?: string; roomId?: string; sessionId?: string }
  | { type: 'task'; relatedToId?: string; taskIds: string[] }

export const aiAgentResultAnchor = (resultId: string, relatedToId?: string | null): DocumentConnectorAnchor => ({
  type: 'aiAgentResult',
  resultId,
  ...(relatedToId ? { relatedToId } : {}),
})

interface UploadFileRequest extends BaseRequest {
  connector: string
  file: Blob
  fileName: string
  folderId: string
  contentType?: string
  anchor?: DocumentConnectorAnchor | null
  saveSyncStatus?: boolean
}

interface SearchItemsRequest extends BaseRequest {
  connector: string
  query: string
  limit?: number
  offset?: number
  cursor?: string
  fileExtensions?: string[]
  ancestorFolderIds?: string[]
}

interface GetFileRequest extends BaseRequest {
  connector: string
  fileId: string
}

const buildQueryString = (params: Record<string, string | number | string[] | undefined | null>): string => {
  const entries = Object.entries(params).flatMap(([key, value]): [string, string][] => {
    if (value === undefined || value === null) {
      return []
    }
    return Array.isArray(value) ? value.map((item) => [key, item]) : [[key, String(value)]]
  })
  const serialized = new URLSearchParams(entries).toString()
  return serialized ? `?${serialized}` : ''
}

export const buildUploadFileFormData = ({
  file,
  fileName,
  folderId,
  contentType,
  anchor,
  saveSyncStatus,
}: Pick<UploadFileRequest, 'file' | 'fileName' | 'folderId' | 'contentType' | 'anchor' | 'saveSyncStatus'>): FormData => {
  const entries: [string, string | Blob][] = [
    ['file', file],
    ['fileName', fileName],
    ['folderId', folderId],
    ...(contentType ? [['contentType', contentType] as [string, string]] : []),
    ...(anchor ? [['anchor', JSON.stringify(anchor)] as [string, string]] : []),
    ...(saveSyncStatus != null ? [['saveSyncStatus', saveSyncStatus ? 'true' : 'false'] as [string, string]] : []),
  ]
  return entries.reduce((formData, [key, value]) => {
    formData.append(key, value)
    return formData
  }, new FormData())
}

export const documentConnectorsApi = createApi({
  reducerPath: 'documentConnectorsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  endpoints: (builder) => ({
    listDocumentConnectorItems: builder.query<Record<string, ListItemsFolderEntry>, ListItemsRequest>({
      query: ({ accountId, connector, folderIds, limit, offset, cursor }) => ({
        url: `/accounts/${accountId}/document-connectors/${connector}/items${buildQueryString({ folderIds, limit, offset, cursor })}`,
      }),
      transformResponse: (response: BaseResponse<Record<string, ListItemsFolderEntry>>) => response.data,
    }),
    uploadDocumentConnectorFile: builder.mutation<DocumentConnectorItem, UploadFileRequest>({
      query: ({ accountId, connector, file, fileName, folderId, contentType, anchor, saveSyncStatus }) => ({
        url: `/accounts/${accountId}/document-connectors/${connector}/files`,
        method: 'POST',
        body: buildUploadFileFormData({ file, fileName, folderId, contentType, anchor, saveSyncStatus }),
      }),
      transformResponse: (response: BaseResponse<DocumentConnectorItem>) => response.data,
    }),
    getDocumentConnectorFile: builder.query<DocumentConnectorItem | null, GetFileRequest>({
      query: ({ accountId, connector, fileId }) => ({
        url: `/accounts/${accountId}/document-connectors/${connector}/files${buildQueryString({ fileId })}`,
        validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
      }),
      transformResponse: (response: BaseResponse<DocumentConnectorItem> | undefined) => response?.data ?? null,
    }),
    searchDocumentConnectorItems: builder.query<SearchItemsResult, SearchItemsRequest>({
      query: ({ accountId, connector, query, limit, offset, cursor, fileExtensions, ancestorFolderIds }) => ({
        url: `/accounts/${accountId}/document-connectors/${connector}/items/search${buildQueryString({
          query,
          limit,
          offset,
          cursor,
          fileExtensions: fileExtensions?.length ? fileExtensions.join(',') : undefined,
          ancestorFolderIds,
        })}`,
      }),
      transformResponse: (response: BaseResponse<SearchItemsResult>) => response.data,
    }),
    listConnectorWorkspaces: builder.query<WorkspaceNode[], { accountId: string; connector: string }>({
      queryFn: async ({ accountId, connector }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/accounts/${accountId}/connectors/${connector}/get-connector-entities/workspace`,
          method: 'POST',
          body: {},
        })
        if (result.error) {
          return { data: [] }
        }
        return { data: (result.data as BaseResponse<WorkspaceNode[]> | undefined)?.data ?? [] }
      },
    }),
  }),
})

export const {
  useListDocumentConnectorItemsQuery,
  useLazyListDocumentConnectorItemsQuery,
  useGetDocumentConnectorFileQuery,
  useLazyGetDocumentConnectorFileQuery,
  useLazySearchDocumentConnectorItemsQuery,
  useUploadDocumentConnectorFileMutation,
  useListConnectorWorkspacesQuery,
} = documentConnectorsApi
